/**
 * Review Mode — the entry point for on-screen annotation.
 *
 * Mounted once in the authenticated Layout. Renders nothing unless the
 * `screenReview` feature flag is on (system/featureFlags) and a user profile
 * is loaded. Available to ALL users — this is the primary way to report
 * anything on screen to support.
 *
 * State lives here, not in a context: nothing outside Review Mode ever needs
 * it, and the page tree re-rendering on every pen stroke would be a cost with
 * no customer.
 *
 * Z-band: 10300–10320 — above stacked app modals (≤10200), below the toast
 * layer (99999).
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  MapPin,
  MoveUpRight,
  Square,
  PenLine,
  Type,
  EyeOff,
  Undo2,
  Redo2,
  Eraser,
  StickyNote,
  X,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  createNote,
  getOrCreateActiveSession,
} from '../../services/screenReview/screenReviewService';
import { captureScreenshot } from '../../utils/screenReview/captureScreenshot';
import { routePattern } from '../../utils/screenReview/routePattern';
import {
  describeElementAt,
  resolveNavAnchor,
} from '../../utils/screenReview/resolveNavAnchor';
import { getRecentConsoleErrors } from '../../utils/screenReview/consoleErrorBuffer';
import type {
  ReviewAnchor,
  ReviewCategory,
  ReviewSeverity,
  ReviewShape,
  ReviewTool,
} from '../../types/screenReview.types';
import { AnnotationOverlay } from './AnnotationOverlay';
import { NoteComposer } from './NoteComposer';
import { ReviewIntro, isReviewIntroDismissed } from './ReviewIntro';
import { OPEN_REVIEW_MODE_EVENT } from '../../utils/supportEvents';

/** Where the note "points": the first shape's focal position. */
function focalPoint(shapes: ReviewShape[]): { x: number; y: number } {
  const s = shapes[0];
  switch (s.kind) {
    case 'pin':
    case 'text':
      return { x: s.x, y: s.y };
    case 'arrow':
      return { x: s.x2, y: s.y2 }; // the tip is what the arrow points AT
    case 'rect':
    case 'redact':
      return { x: s.x + s.w / 2, y: s.y + s.h / 2 };
    case 'freehand': {
      const [x, y] = s.points[0];
      return { x, y };
    }
  }
}

const TOOLS: { tool: ReviewTool; icon: React.ElementType; label: string }[] = [
  { tool: 'pin', icon: MapPin, label: 'Pin — mark a spot' },
  { tool: 'arrow', icon: MoveUpRight, label: 'Arrow — point at something' },
  { tool: 'rect', icon: Square, label: 'Box — frame an area' },
  { tool: 'freehand', icon: PenLine, label: 'Draw freehand' },
  { tool: 'text', icon: Type, label: 'Text label' },
  { tool: 'redact', icon: EyeOff, label: 'Redact — hide sensitive data (burned into the screenshot)' },
];

export const ReviewMode: React.FC = () => {
  const { userProfile } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const location = useLocation();

  const [active, setActive] = React.useState(false);
  const [tool, setTool] = React.useState<ReviewTool | null>(null);
  const [shapes, setShapes] = React.useState<ReviewShape[]>([]);
  const [redoStack, setRedoStack] = React.useState<ReviewShape[]>([]);
  const [capturing, setCapturing] = React.useState(false);
  const [composer, setComposer] = React.useState<{
    screenshot: Blob | null;
    anchor: ReviewAnchor;
    viewportW: number;
    viewportH: number;
    shapes: ReviewShape[];
  } | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [savedFlash, setSavedFlash] = React.useState(false);
  const [showIntro, setShowIntro] = React.useState(false);

  const activate = React.useCallback((): void => {
    setActive(true);
    if (!isReviewIntroDismissed()) setShowIntro(true);
  }, []);

  // Marks are positions on the CURRENT screen. Navigating away invalidates
  // them — carried along, they would float over unrelated UI and, worse, be
  // saved with the new page's anchor. The captured composer survives: its
  // screenshot, shapes and anchor were frozen at capture time.
  React.useEffect(() => {
    setShapes([]);
    setRedoStack([]);
    setTool(null);
  }, [location.pathname]);

  // The Support Launcher's "mark it up on the screen" button activates
  // Review Mode from outside via this event.
  React.useEffect(() => {
    const onOpen = (): void => activate();
    window.addEventListener(OPEN_REVIEW_MODE_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_REVIEW_MODE_EVENT, onOpen);
  }, [activate]);

  if (!userProfile || !isEnabled('screenReview')) return null;

  const reviewerName =
    `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() ||
    userProfile.email ||
    'Unknown user';

  const addShape = (s: ReviewShape): void => {
    setShapes((prev) => [...prev, s]);
    setRedoStack([]);
  };

  const undo = (): void => {
    setShapes((prev) => {
      if (prev.length === 0) return prev;
      setRedoStack((r) => [...r, prev[prev.length - 1]]);
      return prev.slice(0, -1);
    });
  };

  const redo = (): void => {
    setRedoStack((r) => {
      if (r.length === 0) return r;
      setShapes((prev) => [...prev, r[r.length - 1]]);
      return r.slice(0, -1);
    });
  };

  const clearAll = (): void => {
    setShapes([]);
    setRedoStack([]);
  };

  const beginNote = async (): Promise<void> => {
    if (shapes.length === 0 || capturing) return;
    setCapturing(true);
    try {
      // Order matters: describe the element while the screen is live, then
      // capture, THEN open the composer — so the screenshot is of the app.
      const { x, y } = focalPoint(shapes);
      const described = describeElementAt(x, y);
      const nav = resolveNavAnchor(location.pathname);
      const anchor: ReviewAnchor = {
        route: location.pathname,
        routePattern: routePattern(location.pathname),
        navKey: nav?.navKey ?? null,
        navLabel: nav?.navLabel ?? location.pathname,
        xPct: x,
        yPct: y,
        elementText: described.elementText,
        elementPath: described.elementPath,
      };
      const screenshot = await captureScreenshot(shapes);
      setComposer({
        screenshot,
        anchor,
        viewportW: window.innerWidth,
        viewportH: window.innerHeight,
        shapes,
      });
      setTool(null);
    } finally {
      setCapturing(false);
    }
  };

  const saveNote = async (form: {
    body: string;
    category: ReviewCategory;
    severity: ReviewSeverity;
  }): Promise<void> => {
    if (!composer) return;
    setSaving(true);
    setSaveError(null);
    try {
      const session = await getOrCreateActiveSession();
      await createNote({
        sessionId: session.id,
        userId: userProfile.id,
        userAuthUid: userProfile.uid || userProfile.id,
        userName: reviewerName,
        userEmail: userProfile.email || '',
        userRole: String(userProfile.role || ''),
        body: form.body,
        category: form.category,
        severity: form.severity,
        shapes: composer.shapes,
        anchor: composer.anchor,
        viewportW: composer.viewportW,
        viewportH: composer.viewportH,
        screenshot: composer.screenshot,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : '',
        appVersion: typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'unknown',
        consoleErrors: getRecentConsoleErrors(),
      });
      setComposer(null);
      setShapes([]);
      setRedoStack([]);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2500);
    } catch (e: unknown) {
      setSaveError(
        (e as { message?: string }).message ?? 'Could not save the note.',
      );
    } finally {
      setSaving(false);
    }
  };

  const exit = (): void => {
    setActive(false);
    setTool(null);
    setShapes([]);
    setRedoStack([]);
    setComposer(null);
  };

  return (
    <>
      {/* No floating button any more (Jaco 2026-10-19): the red Get Support
          button lives in the top bar (Layout) and opens this mode via
          OPEN_REVIEW_MODE_EVENT, like every other entry point. */}
      {active && (
        <>
          <AnnotationOverlay tool={tool} shapes={shapes} onAddShape={addShape} />

          {/* Toolbar */}
          <div
            data-review-overlay=""
            className="fixed left-1/2 top-16 z-[10308] flex -translate-x-1/2 items-center gap-1 rounded-full bg-neutral-900 px-2 py-1.5 shadow-2xl"
          >
            {TOOLS.map(({ tool: t, icon: Icon, label }) => (
              <button
                key={t}
                type="button"
                onClick={() => setTool((cur) => (cur === t ? null : t))}
                title={label}
                className={`rounded-full p-2 transition-colors ${
                  tool === t
                    ? 'bg-rose-600 text-white'
                    : 'text-neutral-300 hover:bg-neutral-700 hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}

            <span className="mx-1 h-5 w-px bg-neutral-700" />

            <button
              type="button"
              onClick={undo}
              disabled={shapes.length === 0}
              title="Undo last shape"
              className="rounded-full p-2 text-neutral-300 hover:bg-neutral-700 hover:text-white disabled:opacity-40"
            >
              <Undo2 className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={redo}
              disabled={redoStack.length === 0}
              title="Redo"
              className="rounded-full p-2 text-neutral-300 hover:bg-neutral-700 hover:text-white disabled:opacity-40"
            >
              <Redo2 className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={clearAll}
              disabled={shapes.length === 0}
              title="Clear all shapes"
              className="rounded-full p-2 text-neutral-300 hover:bg-neutral-700 hover:text-white disabled:opacity-40"
            >
              <Eraser className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={() => void beginNote()}
              disabled={shapes.length === 0 || capturing}
              title={
                shapes.length === 0
                  ? 'Draw something first, then save it as a note'
                  : 'Save these marks as a note'
              }
              className="ml-1 flex items-center gap-1.5 rounded-full bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-500 disabled:opacity-40"
            >
              {capturing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Capturing…
                </>
              ) : (
                <>
                  <StickyNote className="h-4 w-4" /> Save note
                </>
              )}
            </button>

            <button
              type="button"
              onClick={exit}
              title="Exit Review Mode"
              className="rounded-full p-2 text-neutral-300 hover:bg-neutral-700 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {savedFlash && (
            <div
              data-review-overlay=""
              className="fixed left-1/2 top-28 z-[10308] -translate-x-1/2 rounded-full bg-green-600 px-4 py-1.5 text-sm font-medium text-white shadow-lg"
            >
              Note saved — support has been notified
            </div>
          )}

          {showIntro && <ReviewIntro onClose={() => setShowIntro(false)} />}

          {composer && (
            <NoteComposer
              screenLabel={composer.anchor.navLabel}
              reviewerName={reviewerName}
              saving={saving}
              error={saveError}
              onSave={(f) => void saveNote(f)}
              onCancel={() => setComposer(null)}
            />
          )}
        </>
      )}
    </>
  );
};

export default ReviewMode;
